import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, from, map, of, switchMap } from 'rxjs';

import { ColorPaletteApiService } from '@core/data/api/color-palette-api.service';
import { colorPresetsFromDto } from '@core/data/api/color-palette.mapper';
import { PeopleApiService } from '@core/data/api/people-api.service';
import { DataRepository } from '@core/data/data-repository';
import { Persona } from '@core/models';
import { ReferenceActions } from './reference.actions';

/** Loads app-wide reference data (persona directory, operating systems, colour palette) at startup. */
@Injectable()
export class ReferenceEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);
  private readonly people = inject(PeopleApiService);
  private readonly paletteApi = inject(ColorPaletteApiService);

  // On the `load` trigger, fetch personas and operating systems in parallel and
  // dispatch loadSuccess with both, or loadFailure on any error.
  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReferenceActions.load),
      switchMap(() =>
        forkJoin({
          // With a backend this slice is only the CONTACT OVERLAY -- the four
          // fields a roster row cannot carry, written back by the People form --
          // so it starts EMPTY. Loading the seeded directory here would put
          // prototype email addresses and home addresses behind real people, and
          // invent colleagues for them besides.
          personas: this.people.available()
            ? of([] as Persona[])
            : from(this.repo.listPersonas()),
          operatingSystems: from(this.repo.listOperatingSystems()),
        }).pipe(
          map(({ personas, operatingSystems }) =>
            ReferenceActions.loadSuccess({ personas, operatingSystems }),
          ),
          catchError((err: unknown) =>
            of(ReferenceActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * The company colour palette — read ONCE, here, for the whole app. Weekly
   * Schedule, Training, RPM Plan and Opportunities each used to read it for
   * themselves on page entry, four copies of one list in four places.
   *
   * With no API configured it stays empty (the prototype has no palette, and a
   * seeded copy would be a second palette free to drift from the company's);
   * every picker then says the palette is unavailable. A failed read also leaves
   * it empty — the API error interceptor has already told the person why, and
   * the rest of the reference data is unaffected.
   */
  readonly palette$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReferenceActions.load),
      switchMap(() =>
        this.paletteApi.available()
          ? this.paletteApi.list().pipe(
              map((rows) => ReferenceActions.paletteLoaded({ palette: colorPresetsFromDto(rows) })),
              catchError(() => of(ReferenceActions.paletteLoaded({ palette: [] }))),
            )
          : of(ReferenceActions.paletteLoaded({ palette: [] })),
      ),
    ),
  );
}

/** Normalize any thrown value to a display string for loadFailure. */
function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
